import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import React, { useMemo, useState } from 'react';

import { LoadingButton } from '@mui/lab';
import {
  Box,
  Grid,
  alpha,
  Stack,
  Alert,
  Switch,
  Button,
  Divider,
  TextField,
  Typography,
  IconButton,
} from '@mui/material';

import { useToast } from 'src/hooks/useToast';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import {
  PERMISSION_BUNDLES,
  expandBundlesToPermissions,
  collapsePermissionsToBundles,
} from 'src/utils/permissionBundles';

import { genericApiToaster } from 'src/services/utils';
import {
  addClientCredentials,
  updateClientCredentials,
} from 'src/services/client.credentials.service';

import Iconify from 'src/components/iconify';

function AddUpdateClientCredentials({ hideModal, handleFetchClientCredentials, row }) {
  const { showToast, hideToast } = useToast();
  const { t } = useTranslation();
  const { isSmallScreen } = useResponsiveScreen();
  const [loading, setLoading] = useState(false);

  // State to hold the successful creation payload (contains the unhashed raw secret ONCE)
  const [createdClient, setCreatedClient] = useState(null);

  // Initialize checked categories from pre-existing flat permissions
  const initialBundles = useMemo(() => {
    if (row && row.permissions) {
      return collapsePermissionsToBundles(Array.from(row.permissions));
    }
    return [];
  }, [row]);

  const [selectedBundles, setSelectedBundles] = useState(initialBundles);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid, isDirty },
  } = useForm({
    mode: 'all',
    defaultValues: {
      clientName: row?.clientName || '',
      clientDomain: row?.clientDomain || '',
    },
  });

  const handleToggleBundle = (key) => {
    setSelectedBundles((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleCopyText = (text, messageKey) => {
    navigator.clipboard.writeText(text).then(() => {
      showToast({
        message: t(messageKey),
        severity: 'success',
        props: { hideToast },
      });
    });
  };

  const onSubmitErrorCallback = (error) => {
    if (error.response?.data === 'name exists') {
      showToast({
        message: t('warningMessages.clientNameExists'),
        severity: 'warning',
        props: { hideToast },
      });
    } else if (error.response?.data === 'domain exists') {
      showToast({
        message: t('warningMessages.clientDomainExists'),
        severity: 'warning',
        props: { hideToast },
      });
    } else {
      showToast({
        message: row
          ? t('errorMessages.errorUpdatingClientCredentials')
          : t('errorMessages.errorAddingClientCredentials'),
        severity: 'error',
        props: { hideToast },
      });
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);

    // Expand category keys into the flat permissions array
    const flatPermissions = expandBundlesToPermissions(selectedBundles);
    const payload = {
      ...data,
      permissions: flatPermissions,
    };

    genericApiToaster(
      row ? updateClientCredentials : addClientCredentials,
      row ? [row.id, payload] : payload,
      row
        ? t('successMessages.successUpdatingClientCredentials')
        : t('successMessages.successAddingClientCredentials'),
      '',
      showToast,
      hideToast,
      t,
      (res) => {
        if (!row && res) {
          // If creating a new client, hold the response and display the raw secret
          setCreatedClient(res);
        } else {
          hideModal();
          handleFetchClientCredentials();
          reset();
        }
      },
      onSubmitErrorCallback,
      () => setLoading(false)
    );
  };

  // Multi-colored styling matched to different permission resource categories
  const getBundleColor = (key) => {
    const colors = {
      TICKET_SUBMIT: {
        color: '#2065D1',
        bg: 'rgba(32, 101, 209, 0.04)',
        hover: 'rgba(32, 101, 209, 0.08)',
      },
      TICKET_RESOLVE: {
        color: '#2065D1',
        bg: 'rgba(32, 101, 209, 0.04)',
        hover: 'rgba(32, 101, 209, 0.08)',
      },
      TICKET_DELETE: {
        color: '#2065D1',
        bg: 'rgba(32, 101, 209, 0.04)',
        hover: 'rgba(32, 101, 209, 0.08)',
      },
      SCOPE_DEPARTMENT: {
        color: '#2065D1',
        bg: 'rgba(32, 101, 209, 0.04)',
        hover: 'rgba(32, 101, 209, 0.08)',
      },
      DEPARTMENT_CONTROL: {
        color: '#00A76F',
        bg: 'rgba(0, 167, 111, 0.04)',
        hover: 'rgba(0, 167, 111, 0.08)',
      },
      USER_CONTROL: {
        color: '#00A76F',
        bg: 'rgba(0, 167, 111, 0.04)',
        hover: 'rgba(0, 167, 111, 0.08)',
      },
      ROLE_CONTROL: {
        color: '#00A76F',
        bg: 'rgba(0, 167, 111, 0.04)',
        hover: 'rgba(0, 167, 111, 0.08)',
      },
      FORM_CONTROL: {
        color: '#ed6c02',
        bg: 'rgba(237, 108, 2, 0.04)',
        hover: 'rgba(237, 108, 2, 0.08)',
      },
      CREDENTIALS_CONTROL: {
        color: '#9c27b0',
        bg: 'rgba(156, 39, 176, 0.04)',
        hover: 'rgba(156, 39, 176, 0.08)',
      },
      CSV_CONTROL: {
        color: '#9c27b0',
        bg: 'rgba(156, 39, 176, 0.04)',
        hover: 'rgba(156, 39, 176, 0.08)',
      },
    };
    return (
      colors[key] || {
        color: '#00A76F',
        bg: 'rgba(0, 167, 111, 0.04)',
        hover: 'rgba(0, 167, 111, 0.08)',
      }
    );
  };

  // --- ONE-TIME CREDENTIAL DISPLAY PANEL ---
  if (createdClient) {
    return (
      <Stack spacing={2.5} mt={1} p={1}>
        <Alert
          severity="warning"
          variant="filled"
          sx={{ borderRadius: '12px', fontWeight: 'bold' }}
        >
          {t('pages.clientCredentialsPage.disclaimerOneTimeDisplay') ||
            'Store this client secret securely. It will only be displayed once and can never be retrieved again.'}
        </Alert>

        <TextField
          fullWidth
          label={t('pages.clientCredentialsPage.clientName')}
          value={createdClient.clientName}
          InputProps={{ readOnly: true }}
          size={isSmallScreen ? 'small' : 'medium'}
        />

        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            fullWidth
            label={t('pages.clientCredentialsPage.details.clientId')}
            value={createdClient.clientId}
            InputProps={{ readOnly: true }}
            size={isSmallScreen ? 'small' : 'medium'}
          />
          <IconButton
            onClick={() =>
              handleCopyText(
                createdClient.clientId,
                'pages.clientCredentialsPage.clientIdCopied'
              )
            }
          >
            <Iconify icon="heroicons:clipboard-document-list" width={24} height={24} />
          </IconButton>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            fullWidth
            label={t('pages.clientCredentialsPage.details.clientSecret')}
            value={createdClient.clientSecret} // Displays the unhashed raw secret ONCE
            InputProps={{ readOnly: true }}
            size={isSmallScreen ? 'small' : 'medium'}
          />
          <IconButton
            onClick={() =>
              handleCopyText(
                createdClient.clientSecret,
                'pages.clientCredentialsPage.clientSecretCopied'
              )
            }
          >
            <Iconify icon="heroicons:clipboard-document-list" width={24} height={24} />
          </IconButton>
        </Stack>

        <Button
          fullWidth
          variant="contained"
          onClick={() => {
            hideModal();
            handleFetchClientCredentials();
            reset();
          }}
          size={isSmallScreen ? 'small' : 'medium'}
          sx={{
            bgcolor: '#00A76F',
            '&:hover': { bgcolor: '#008F5D' },
            fontWeight: 'bold',
            borderRadius: '12px',
          }}
        >
          {t('buttons.done')}
        </Button>
      </Stack>
    );
  }

  // --- STANDARD CREATION / UPDATE FORM ---
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={2.5} mt={1}>
        <TextField
          fullWidth
          label={t('pages.clientCredentialsPage.clientName')}
          {...register('clientName', { required: t('formControl.fieldRequired') })}
          error={!!errors.clientName}
          helperText={errors.clientName?.message}
          size={isSmallScreen ? 'small' : 'medium'}
          sx={{
            '& .MuiInputBase-root': { fontSize: isSmallScreen ? '0.7rem' : '1rem' },
            '& .MuiFormLabel-root': { fontSize: isSmallScreen ? '0.7rem' : '1rem' },
          }}
        />

        <TextField
          fullWidth
          label={t('pages.clientCredentialsPage.clientDomain')}
          {...register('clientDomain', { required: t('formControl.fieldRequired') })}
          error={!!errors.clientDomain}
          helperText={errors.clientDomain?.message}
          size={isSmallScreen ? 'small' : 'medium'}
          sx={{
            '& .MuiInputBase-root': { fontSize: isSmallScreen ? '0.7rem' : '1rem' },
            '& .MuiFormLabel-root': { fontSize: isSmallScreen ? '0.7rem' : '1rem' },
          }}
        />

        <Divider sx={{ borderStyle: 'dashed' }} />

        {/* M2M Permissions Console */}
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
            {t('pages.clientCredentialsPage.permissionsTitle') ||
              'M2M Permissions / Scopes'}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 2.5, fontWeight: 600 }}
          >
            {t('pages.clientCredentialsPage.permissionsSubtitle') ||
              'Enable specific administrative categories to grant access to your external client.'}
          </Typography>

          <Grid container spacing={2}>
            {Object.keys(PERMISSION_BUNDLES).map((key) => {
              const bundle = PERMISSION_BUNDLES[key];
              const isChecked = selectedBundles.includes(key);
              const palette = getBundleColor(key); // Resolves your distinct category color

              return (
                <Grid item xs={12} sm={6} key={key}>
                  <Box
                    onClick={() => handleToggleBundle(key)}
                    sx={{
                      p: 2,
                      borderRadius: '16px',
                      border: '1.5px solid',
                      borderColor: isChecked ? palette.color : 'rgba(0,0,0,0.06)',
                      bgcolor: isChecked ? palette.bg : 'background.paper',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                      cursor: 'pointer',
                      boxShadow: isChecked
                        ? `0 4px 12px 0 ${alpha(palette.color, 0.05)}`
                        : 'none',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        borderColor: palette.color,
                        bgcolor: palette.hover,
                        boxShadow: `0 4px 12px 0 ${alpha(palette.color, 0.05)}`,
                      },
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={2}
                      sx={{ minWidth: 0, flexGrow: 1 }}
                    >
                      {/* Left Side: Category Icon */}
                      <Box
                        sx={{
                          p: 1.25,
                          borderRadius: '12px',
                          bgcolor: isChecked
                            ? alpha(palette.color, 0.12)
                            : 'rgba(0,0,0,0.03)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'background-color 0.2s',
                        }}
                      >
                        <Iconify
                          icon={bundle.icon}
                          width={22}
                          sx={{
                            color: isChecked ? palette.color : 'text.secondary',
                            transition: 'color 0.2s',
                          }}
                        />
                      </Box>

                      {/* Center: Title Only (Omitted description to prevent text overflow) */}
                      <Stack spacing={0} sx={{ minWidth: 0, flexGrow: 1 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 800, fontSize: '0.85rem' }}
                        >
                          {t(bundle.label) || bundle.defaultLabel}
                        </Typography>
                      </Stack>
                    </Stack>

                    <Switch
                      checked={isChecked}
                      onChange={() => handleToggleBundle(key)}
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: palette.color,
                          '&:hover': {
                            backgroundColor: alpha(palette.color, 0.08),
                          },
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: palette.color,
                          opacity: 0.5,
                        },
                      }}
                    />
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        <LoadingButton
          type="submit"
          fullWidth
          variant="contained"
          loading={loading}
          disabled={
            !isValid ||
            (!isDirty &&
              selectedBundles.length === initialBundles.length &&
              selectedBundles.every((v) => initialBundles.includes(v)))
          }
          size={isSmallScreen ? 'small' : 'medium'}
          sx={{
            fontSize: isSmallScreen && '0.7rem',
            fontWeight: 'bold',
            borderRadius: '12px',
          }}
        >
          {row ? t('buttons.update') : t('buttons.submit')}
        </LoadingButton>
      </Stack>
    </form>
  );
}

AddUpdateClientCredentials.propTypes = {
  hideModal: PropTypes.func,
  handleFetchClientCredentials: PropTypes.func,
  row: PropTypes.object,
};

export default AddUpdateClientCredentials;
